import { BigDollarInput } from "@/components/BigDollarInput/BigDollarInput";
import { Doc } from "@/components/Doc/Doc";
import { LabelExplainer } from "@/components/LabelExplainer/LabelExplainer";
import {
  cashFlowAtom,
  getMultipleFromPrice,
  listingPurchaseMultipleAtom,
  targetPurchaseMultipleAtom,
  targetPurchasePriceAtom,
} from "@/financeModels/liteAtoms";
import { inputFontFamily } from "@/theme";
import { isBetween, toNumberOrNull } from "@/utils/math";
import {
  ActionIcon,
  Box,
  Button,
  Grid,
  Group,
  NumberInput,
  SegmentedControl,
  Stack,
  Text,
} from "@mantine/core";
import { useAtom } from "jotai";
import { format } from "mathjs";
import { AiOutlineFunction } from "react-icons/ai";
import {
  TbSquareRoundedMinusFilled,
  TbSquareRoundedPlusFilled,
} from "react-icons/tb";
import { QuestionnaireSection } from "./QuestionnaireSection";

export const SegmentTargetPurchaseMultiple = () => {
  const [targetPurchasePrice] = useAtom(targetPurchasePriceAtom);
  const [listingPurchaseMultiple] = useAtom(listingPurchaseMultipleAtom);
  const [cashFlow] = useAtom(cashFlowAtom);
  const [targetPurchaseMultiple, setTargetPurchaseMultiple] = useAtom(
    targetPurchaseMultipleAtom,
  );

  const purchaseMultiple = toNumberOrNull(targetPurchaseMultiple) ?? 0;

  // round listingPurchaseMultiple to the nearest 0.25
  const listingPurchaseMultipleRounded =
    Math.round((toNumberOrNull(listingPurchaseMultiple) ?? 0) * 4) / 4;

  const segmentedData = [1, 2, 3].map((i) => {
    const value = listingPurchaseMultipleRounded - i * 0.25;
    return {
      value: value.toString(),
      label:
        value > 0 && value < 10 ? value.toFixed(2).toString() + "x" : "- - -",
    };
  });

  const segmentIncludePurchaseMultiple = segmentedData.find(
    (d) => d.value === purchaseMultiple.toString(),
  );

  const isValidListingPurchaseMultiple = isBetween(
    listingPurchaseMultiple,
    1,
    9,
  );

  return (
    <QuestionnaireSection
      icon={AiOutlineFunction}
      label={
        <LabelExplainer
          label="Target Purchase Multiple"
          showIcon
          explanation={
            <Doc path="@/docs/pages/home/target-purchase-multiple.mdx" />
          }
        />
      }
    >
      <Box>
        {!isValidListingPurchaseMultiple && (
          <Stack justify="center" align="center" h="90">
            <Text c="dimmed" px={{ base: 0, md: "15%" }} ta="center">
              Once you enter a valid Listing Price and Cash Flow (SDE) your
              purchase multiple options will appear here.
            </Text>
          </Stack>
        )}
        {isValidListingPurchaseMultiple && (
          <Grid gutter={18} align="center" justify="center">
            <Grid.Col span="content">
              <Group gap={4}>
                <ActionIcon
                  variant="transparent"
                  color={"accent.6"}
                  onClick={() => {
                    setTargetPurchaseMultiple(purchaseMultiple - 0.01);
                  }}
                >
                  <TbSquareRoundedMinusFilled size={30} />
                </ActionIcon>
                <NumberInput
                  styles={{
                    input: { textAlign: "center", padding: 0 },
                  }}
                  allowLeadingZeros={true}
                  allowNegative={false}
                  decimalScale={2}
                  hideControls
                  size={"lg"}
                  value={purchaseMultiple}
                  w={90}
                  suffix="x"
                  clampBehavior="strict"
                  min={0}
                  max={9}
                  fixedDecimalScale
                  onChange={(value) => {
                    setTargetPurchaseMultiple(Number(value));
                  }}
                />
                <ActionIcon
                  variant="transparent"
                  color={"accent.6"}
                  onClick={() => {
                    setTargetPurchaseMultiple(purchaseMultiple + 0.01);
                  }}
                >
                  <TbSquareRoundedPlusFilled size={30} />
                </ActionIcon>
              </Group>
            </Grid.Col>
            <Grid.Col span={{ md: "content" }}>
              <Stack>
                <SegmentedControl
                  color={
                    segmentIncludePurchaseMultiple ? "accent" : "transparent"
                  }
                  value={purchaseMultiple.toString()}
                  size="md"
                  data={segmentedData}
                  onChange={(value) => {
                    setTargetPurchaseMultiple(Number(value));
                  }}
                  styles={{ label: { fontSize: 16 } }}
                  ff={inputFontFamily}
                />
                <Button
                  size="md"
                  variant="light"
                  disabled={!isBetween(listingPurchaseMultiple, 0, 10)}
                  onClick={() => {
                    setTargetPurchaseMultiple(listingPurchaseMultiple);
                  }}
                >
                  Reset
                  {isBetween(listingPurchaseMultiple, 0, 10)
                    ? " (" +
                      format(listingPurchaseMultiple, {
                        notation: "fixed",
                        precision: 2,
                      }) +
                      "x)"
                    : ""}
                </Button>
              </Stack>
            </Grid.Col>
            <Grid.Col span="content">
              <BigDollarInput
                value={toNumberOrNull(targetPurchasePrice) ?? ""}
                pos={"relative"}
                top={-10}
                w={190}
                size={"lg"}
                label="Target Purchase Price:"
                onChange={(value) => {
                  const multiple = getMultipleFromPrice({
                    price: toNumberOrNull(value ?? 0),
                    cashFlow,
                  });
                  setTargetPurchaseMultiple(multiple);
                }}
              />
            </Grid.Col>
          </Grid>
        )}
      </Box>
    </QuestionnaireSection>
  );
};
